/**
 * Bloom Protection Component
 * Handles the Yes/No radio button interface for bloom protection
 * Manages tied_product functionality behind the scenes
 */

class BloomProtectionComponent extends HTMLElement {
  constructor() {
    super();
    this.isInitializing=true;
    this.builder = this.closest('product-main-builder');
    this.productIndex = this.dataset.productIndex;
    this.tiedProductData = JSON.parse(this.querySelector('[data-tied-product-json]').textContent);
    this.variantInput = this.querySelector('.single-option-selector');
    this.mainProductVariant = null;
    this.closest('.bloom-protection-section').classList.add('hidden-by-variant');
    this.selectNoSilent();
    this.bindEvents();
    this.builder.addEventListener('mainVariantChange', this.handleMainVariantChange.bind(this));
    
    const currentVariant = this.builder.getCurrentVariant();
    if (currentVariant) {
      this.updateVisibility(currentVariant);
    }
    this.updateUpgradePrice();

    this.isInitializing=false;
  }

  bindEvents() {
    this.querySelectorAll('.bloom-protection-radio').forEach(radio => {
      radio.addEventListener('change', this.handleRadioChange.bind(this));
    });
    
    const infoBtn = this.querySelector('.bloom-protection-info-btn');
    if (infoBtn) {
      infoBtn.addEventListener('click', this.openInfoModal.bind(this));
    }
  }

  handleMainVariantChange(event) {
    this.mainProductVariant = event.detail.variant;
    this.updateVisibility(event.detail.variant);
    this.updateUpgradePrice();

    const yesRadio = this.querySelector('input[value="Yes"]:checked');
    if (yesRadio) {
      this.updateBloomProtectionVariant();
    }
  }

  handleRadioChange(event) {
    const isYes = event.target.value === 'Yes';
    this.querySelectorAll('.bloom-protection-radio').forEach(radio => {
      if (radio !== event.target) {
        radio.checked = false;
      }
    });
    
    if (isYes) {
      this.enableBloomProtection();
    } else {
      this.disableBloomProtection();
    }
    
    this.builder.dispatchEvent(new CustomEvent('stepChange'));
    this.builder.dispatchEvent(new CustomEvent('bundleChange'));
  }

  enableBloomProtection() {
    this.querySelectorAll('input[data-name]').forEach(input => {
      if (input.dataset.name && input.dataset.name.includes('items[')) {
        input.setAttribute('name', input.dataset.name);
        input.removeAttribute('disabled');
      }
    });
    
    this.updateBloomProtectionVariant();
  }

  disableBloomProtection() {
    this.querySelectorAll('input[name]').forEach(input => {
      if (input.name.includes('items[')) {
        input.setAttribute('data-name', input.name);
        input.removeAttribute('name');
      }
    });
    
    this.removeAttribute('data-price');
  }

  disableBloomProtectionSilent() {
    this.querySelectorAll('input[name]').forEach(input => {
      if (input.name.includes('items[')) {
        input.setAttribute('data-name', input.name);
        input.removeAttribute('name');
      }
    });
    
    this.removeAttribute('data-price');
  }

  resolveBloomVariant(mainVariant) {
    if (!mainVariant) return null;

    let sizeOption = null;

    if (mainVariant.option1 && this.looksLikeSize(mainVariant.option1)) {
      sizeOption = mainVariant.option1;
    } else if (mainVariant.option2 && this.looksLikeSize(mainVariant.option2)) {
      sizeOption = mainVariant.option2;
    } else if (mainVariant.option3 && this.looksLikeSize(mainVariant.option3)) {
      sizeOption = mainVariant.option3;
    }

    let bloomVariant = null;
    if (sizeOption) {
      bloomVariant = this.tiedProductData.variants.find(v =>
        v.option1 === sizeOption ||
        v.title.includes(sizeOption) ||
        v.option1.includes(sizeOption)
      );
    }

    return bloomVariant || this.tiedProductData.variants[0];
  }

  updateBloomProtectionVariant() {
    const mainVariant = this.mainProductVariant || this.builder.getCurrentVariant();
    if (!mainVariant) return;

    const bloomVariant = this.resolveBloomVariant(mainVariant);

    if (this.variantInput && bloomVariant) {
      this.variantInput.value = bloomVariant.id;
    }

    this.setAttribute('data-price', bloomVariant.price);
  }

  // Test formatting: the Yes card quotes the size-matched upgrade price
  // (the span only renders when the section's test formatting is on)
  updateUpgradePrice() {
    const priceElement = this.querySelector('.bloom-upgrade-price');
    if (!priceElement) return;

    const bloomVariant = this.resolveBloomVariant(this.mainProductVariant || this.builder.getCurrentVariant());
    if (bloomVariant) {
      priceElement.textContent = '+$' + Math.round(bloomVariant.price / 100);
    }
  }

  looksLikeSize(option) {
    return option.includes('×') || option.includes('x') || /\d/.test(option);
  }

  updateVisibility(variant) {
    const section = this.closest('.bloom-protection-section');
    
    if (!variant) {
      section.classList.add('hidden-by-variant');
      this.selectNoSilent();
      return;
    }

    let sizeOption = null;
    if (variant.option1 && this.looksLikeSize(variant.option1)) {
      sizeOption = variant.option1;
    } else if (variant.option2 && this.looksLikeSize(variant.option2)) {
      sizeOption = variant.option2;
    } else if (variant.option3 && this.looksLikeSize(variant.option3)) {
      sizeOption = variant.option3;
    }

    const shouldShow = this.isSizeLargerThan6x8(sizeOption);
    
    if (shouldShow) {
      section.classList.remove('hidden-by-variant');
      this.clearSelection();
    } else {
      section.classList.add('hidden-by-variant');
      this.selectNoSilent();
    }
  }

  isSizeLargerThan6x8(sizeOption) {
    if (!sizeOption) return false;

    const sizeRegex = /(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i;
    const match = sizeOption.match(sizeRegex);
    
    if (!match) return false;

    const width = parseFloat(match[1]);
    const height = parseFloat(match[2]);
    
    const selectedArea = width * height;
    const minArea = 6 * 8;
    
    return selectedArea > minArea;
  }

  clearSelection() {
    this.querySelectorAll('.bloom-protection-radio').forEach(radio => {
      radio.checked = false;
    });
    
    this.disableBloomProtectionSilent();
    
    this.builder.dispatchEvent(new CustomEvent('stepChange'));
    this.builder.dispatchEvent(new CustomEvent('bundleChange'));
  }

  selectNoSilent() {
    const noRadio = this.querySelector('input[value="No"]');
    if (noRadio) {
      noRadio.checked = true;
    }
    this.disableBloomProtectionSilent();

    if(!this.isInitializing) {
      this.builder.dispatchEvent(new CustomEvent('stepChange'));
      this.builder.dispatchEvent(new CustomEvent('bundleChange'));
    }
  }

  openInfoModal(event) {
    event.preventDefault();
    
    const modalContent = `
      <div class="bloom-protection-modal-content">
        <div class="bloom-protection-modal-image">
          <img src="https://cdn.shopify.com/s/files/1/0508/2025/1818/files/CZ0A0935.jpg" alt="Bloom Protection">
        </div>
        <div class="bloom-protection-modal-text">
          <h2>What is Bloom Protection?</h2>
          <p class="subtitle">Protect and safeguard your precious blooms during shipping and preservation.</p>
          <h3>Damaged or Unworkable Blooms</h3>
          <p>If your flowers arrive in poor condition or do not survive the preservation process due to various natural factors, we'll replace them.</p>
          <h3>Lost or Stolen Blooms</h3>
          <p>If your flowers are lost or stolen during shipment, we've got you covered.</p>
          <h3>Conditions for Coverage</h3>
          <p>Blooms must arrive within 5 days of your event. Shipping instructions must be followed. A tracking number is required for lost bloom claims.</p>
        </div>
      </div>
    `;
    
    $s.openModal(modalContent, {
      cssClass: ['bloom-protection-info-modal', 'two-column-modal'],
      closeLabel: 'Close'
    });
  }
}

customElements.define('bloom-protection-component', BloomProtectionComponent);